import { getTextNodes, isHtmlString } from '@app/utils/dom';
import { escapeRegexp } from '@app/utils/escape-regexp';
import { HighlightOptions } from '@app/utils/highlight/highlight-options';
import { MatchType } from '@app/utils/highlight/match-type';
import { isNotBlank } from '@app/utils/string';

/**
 * Highlight the given term inside the given content.
 *
 * @param content String that is search for the term, can be either plain text or html
 * @param term String to look for
 * @param options Default { caseSensitive: false, type: 'Multi' }
 */
export function highlight(content: string, term: string, options: Partial<HighlightOptions> = {}): string {
  let returnValue: string = content;

  // If the term is empty, nothing needs to be done.
  if (isNotBlank(term)) {
    const normalizedOptions = normalizeOptions(options);

    if (isHtmlString(content)) {
      returnValue = highlightInHtml(content, term, normalizedOptions);
    } else {
      returnValue = replace(content, term, normalizedOptions);
    }
  }

  return returnValue;
}

/**
 * Create the regex that is used to find the matches.
 *
 * @param term String to look for
 * @param caseSensitive Whether the search should be case sensitive
 * @param type What type of matcher to create
 */
function getMatchRegex(term: string, caseSensitive: boolean, type: MatchType = 'Single') {
  let regex: RegExp;

  const escaped = escapeRegexp(term ?? '');
  const caseFlag: string = caseSensitive === false ? 'i' : '';

  switch (type) {
    case 'Single': {
      regex = new RegExp(escaped, caseFlag);
      break;
    }
    case 'StartsWith': {
      regex = new RegExp('^' + escaped, caseFlag);
      break;
    }
    case 'Multi': {
      regex = new RegExp(escaped, 'g' + caseFlag);
      break;
    }
    default: {
      // default will be a case-insensitive Multi
      regex = new RegExp(escaped, 'gi');
    }
  }

  return regex;
}

/**
 * This function highlights only the text that is contained by the given html string (i.e. that
 * no html nodes will be marked).
 *
 * @param value Some html string
 * @param term String to look for
 * @param options Options that define how to match and how to apply a mark
 */
function highlightInHtml(value: string, term: string, options: HighlightOptions): string {
  let returnValue: string = value;

  const MARK_START = '_MARK_START_';
  const MARK_END = '_MARK_END_';

  const replaceOptions = {
    ...options,
    surroundFunction: (value) => `${MARK_START}${value}${MARK_END}`,
  };

  const container = document.createElement('div');

  container.innerHTML = value;

  const textNodes = getTextNodes(container);

  // Check only the first text node if only a match at the beginning should be highlighted.
  if (options.type === 'StartsWith' && textNodes.length > 0) {
    textNodes[0].textContent = replace(textNodes[0].textContent, term, replaceOptions);
  } else {
    for (const node of textNodes) {
      const original = node.textContent;

      node.textContent = replace(node.textContent ?? '', term, {
        ...options,
        surroundFunction: (value) => `${MARK_START}${value}${MARK_END}`,
      });

      // Stop if this is a match and only one match should be highlighted.
      if (options.type === 'Single' && original !== node.textContent) {
        break;
      }
    }

    returnValue = container.innerHTML
      .replace(new RegExp(MARK_START, 'g'), `<mark class="${options.className}">`)
      .replace(new RegExp(MARK_END, 'g'), '</mark>');
  }

  return returnValue;
}

/**
 * Merge the given options with the default options and return a complete list of options.
 */
function normalizeOptions(options?: Partial<HighlightOptions>): HighlightOptions {
  const normalized: HighlightOptions = {
    caseSensitive: false,
    className: 'highlight',
    surroundFunction: (value) => `<mark class="${normalized.className}">${value}</mark>`,
    type: 'Multi',
    ...(options ?? {}),
  };

  return normalized;
}

/**
 * Surround all substrings that match the given term.
 *
 * @param value String to search in
 * @param term String to look for
 * @param options Options that define how to match and how to apply a mark
 */
function replace(value: string, term: string, options: HighlightOptions): string {
  const regex = getMatchRegex(term, options.caseSensitive, options.type);

  return value.replace(regex, (match) => options.surroundFunction(match));
}
